import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import { LindList } from './KindList';
import * as S from './AdCardNew.styled';
import { AdStatus } from '@/types';
import { Ad } from '@/api/types/common.types';
import { useIsShop } from '@/hooks/useIsShop';
import { addQueryParamToPath, formatNumber } from '@/utils/helpers';
import { getMessageTime } from '@/utils/datetime.utils';
import {
  HeartActiveIcon,
  HeartIconInactive,
} from '@/components/UI/Icons/Icons';
import { useBookmark } from '@/hooks/useBookmark';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';
import { useUserFavorites } from '@/features/BookmarkCollections/hooks';
import { useBookmarkSelectionStore } from '@/features/BookmarkCollections/store';
import { Typography } from '@/components/UI/Typography/Typography.styles';
import { ROUTES } from '@/constants/routes';
import NoSsr from '@/components/NoSsr/NoSsr';
import { useResponsive } from '@/hooks/useResponsive';
import { TransitionSourceBlockEnum } from '@/api/types/ads.types';
import { QUERY } from '@/constants/common';

interface AdCardNewProps {
  ad?: Ad;
  priority?: boolean;
  hasBookmarkButton?: boolean;
  onRemoveBookmark?: (ad: Ad) => void;
  onAddBookmark?: (ad: Ad) => void;
  transitionSource?: TransitionSourceBlockEnum;
}

export const AdCardNew = ({
  ad,
  onRemoveBookmark,
  onAddBookmark,
  priority = false,
  hasBookmarkButton = true,
  transitionSource,
}: AdCardNewProps) => {
  const isShop = useIsShop(undefined, { cacheOnly: true });
  const router = useRouter();
  const { t, i18n } = useTranslation();
  const { refetch } = useUserFavorites({ skip: true });
  const { isBookmarked, toggleBookmark } = useBookmark({ ad });
  const { toggleSelectedItem, isSelected, isSelectionActive } =
    useBookmarkSelectionStore();
  const { isDesktop } = useResponsive();

  const path = ad?.path ?? '';
  const href = transitionSource
    ? addQueryParamToPath(path, QUERY.transitionSourceBlock, transitionSource)
    : path;
  const isBookmarksPage = router.pathname === ROUTES.bookmarks;
  const isShopAdBookmark = !isBookmarked && isShop && ad?.shop;
  const isBookmarkButtonVisible = hasBookmarkButton && !isSelectionActive;

  const statusText = useMemo(() => {
    switch (ad?.status) {
      case AdStatus.Expired:
        return t('pages.ad.ad_inactive');
      case AdStatus.Archived:
        return t('pages.ad.ad_archived');
      default:
        return '';
    }
  }, [ad?.status]);

  const hasStatus = statusText.length > 0;

  const toggleFavorite = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (!ad) return;

    await toggleBookmark();
    await refetch();

    if (ad.isBookmarked) {
      onRemoveBookmark?.(ad);
    } else {
      onAddBookmark?.(ad);
    }
  };

  const toggleSelection = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (!ad) return;

    toggleSelectedItem(String(ad.legacyResourceId));
  };

  if (!ad) {
    return (
      <S.CardPlaceholder>
        <S.ImageWrapper>
          <SkeletonLoader height="100%" />
        </S.ImageWrapper>
        <SkeletonLoader height={15} width={130} />
        <SkeletonLoader height={15} width={120} />
        <SkeletonLoader height={15} width={100} />
        <SkeletonLoader height={15} width={100} />
      </S.CardPlaceholder>
    );
  }

  return (
    <S.Card
      href={href}
      onClick={isSelectionActive ? toggleSelection : undefined}
      dataTestId="ad-card"
      data-stat={!isBookmarksPage ? 'ad-card-link' : undefined}
    >
      <S.ImageWrapper>
        {hasStatus ? (
          <S.StatusLabel data-testid="ad-card-photo-overlay">
            <Typography.Body>{statusText}</Typography.Body>
          </S.StatusLabel>
        ) : null}
        {isBookmarkButtonVisible && (
          <S.BookmarkButton
            onClick={toggleFavorite}
            className="products-bookmarking"
            data-stat={isShopAdBookmark ? 'shop-ad-bookmarking' : undefined}
            data-testid="bookmark-button"
          >
            {isBookmarked ? (
              <HeartActiveIcon stroke="none" data-testid="is-bookmarked" />
            ) : (
              <HeartIconInactive
                data-testid="is-not-bookmarked"
                className="add_bookmark bookmark__link"
              />
            )}
          </S.BookmarkButton>
        )}

        {isSelectionActive && (
          <S.Checkbox
            onClick={toggleSelection}
            $isChecked={isSelected(String(ad.legacyResourceId))}
          />
        )}

        <S.Image
          alt={ad.title}
          title={ad.title}
          src={
            isDesktop && ad.desktopPhoto ? ad.desktopPhoto.url : ad.photo.url
          }
          {...(priority ? { fetchPriority: 'high' } : { loading: 'lazy' })}
        />

        {ad.shop && <S.Label>{t('common.shop')}</S.Label>}
      </S.ImageWrapper>
      {ad.price !== null && (
        <S.Price data-testid="ad-card-price">
          {formatNumber(ad.price, 2, true) || 0} ₼
        </S.Price>
      )}
      <S.Title $hasPadding={ad.price === null} data-testid="ad-card-title">
        {ad.title}
      </S.Title>
      <S.Footer>
        <S.DateAndRegion data-testid="ad-card-additional">
          {`${ad.region}, `}
          <NoSsr fallback={<SkeletonLoader as="span" width={90} height={15} />}>
            {getMessageTime(new Date(ad.updatedAt), i18n, true)}
          </NoSsr>
        </S.DateAndRegion>
        <LindList kinds={ad.kinds} />
      </S.Footer>
    </S.Card>
  );
};
